/**
 * Compile-time drop reporter. Query builders intentionally drop what they cannot send (a filter
 * leaf on a column the catalog lacks, an expression ref with no body); this makes those drops
 * visible without changing them. Deduped per page load on (kind, column, reason): the first
 * occurrence is logged, later ones only bump the count. `window.__edgeQueryDrops` is the same
 * list, for test harnesses to read.
 */
export type QueryDropKind = 'filter' | 'expression'

export interface QueryDrop {
  kind: QueryDropKind
  column: string
  reason: string
  count: number
}

declare global {
  interface Window {
    __edgeQueryDrops?: QueryDrop[]
  }
}

const byKey = new Map<string, QueryDrop>()
const list: QueryDrop[] = []
let pending: string[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null

const FLUSH_MS = 250

// The harness typechecks this file without Vite's `ImportMeta` augmentation.
function isDev(): boolean {
  return (import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV === true
}

function flush(): void {
  flushTimer = null
  if (!pending.length) return
  const lines = pending
  pending = []
  console.warn(`[query-drop] ${lines.length} new: ${lines.join('; ')}`)
}

export function reportQueryDrop(drop: Omit<QueryDrop, 'count'>): void {
  const key = `${drop.kind}:${drop.column}:${drop.reason}`
  const seen = byKey.get(key)
  if (seen) {
    seen.count += 1
    return
  }
  const rec: QueryDrop = { ...drop, count: 1 }
  byKey.set(key, rec)
  list.push(rec)
  if (typeof window !== 'undefined') window.__edgeQueryDrops = list
  if (!isDev()) return
  pending.push(`${drop.kind} ${drop.column} (${drop.reason})`)
  if (flushTimer === null) flushTimer = setTimeout(flush, FLUSH_MS)
}

export function queryDrops(): readonly QueryDrop[] {
  return list
}

export function resetQueryDrops(): void {
  byKey.clear()
  list.length = 0
  pending = []
  if (flushTimer !== null) {
    clearTimeout(flushTimer)
    flushTimer = null
  }
}
